import React, {
	useEffect, useRef, useState,
} from 'react';
import {
	Image,
} from 'models';
import './camera.scss';
import Button, {
	ButtonColor,
} from '../button';
import {
	MdOutlineClose,
} from 'react-icons/md';
import {
	Colors,
} from 'utils/color/ColorPalette';
import CameraIcon from 'assets/img/app.camera.svg';
import {
	useSettingsService,
} from 'services';
import {
	SettingKey,
} from 'utils/database';


import {
	isMobile,
} from 'utils/device';
import Typography, {
	TypographyType,
} from '../typography/karim/Typography';
import {
	convertFileToBase64,
} from '../../utils/file';
import LoadingWheel from '../loadingWheel/LoadingWheel';

interface Props {
	onPhotoTaken?: (images: Image[]) => void;
	onImageRemoved?: (images: Image) => void;
	initialImages?: Image[];
}

/**
 * A component for handeling the action of taking a picture
 * on the phone, tablet or computer
 * @constructor
 */
const Camera = ({
	onPhotoTaken = (_images: Image[]) => {},
	onImageRemoved,
	initialImages = [],
}: Props): JSX.Element => {
	const {
		data: settings,
		patch,
	} = useSettingsService();
	const videoRef = useRef(null);
	const photoRef = useRef(null);
	const isOnMobile = isMobile();
	const [imageData, setImageData] = useState<Image[]>(initialImages);
	const [videoResolution, setVideoResolution] = useState<{height: number, width: number}>();
	const [hoverIndex, setHoverIndex] = useState<number>(-1);
	const [videoStream, setVideoStream] = useState<MediaStream | undefined>(undefined);
	const [availableSources, setAvailableSources] = useState<MediaDeviceInfo[]>([]);
	const [isStartingCamera, setIsStartingCamera] = useState(false);
	const [isConvertingFile, setIsConvertingFile] = useState(false);


	/**
	 * Drawing onto the canvas,
	 * so that we can take the image
	 */
	const paintToCanvas = () => {
		const video = videoRef.current;
		const photo: any = photoRef.current;
		const ctx = photo.getContext('2d');

		return setInterval(() =>
			ctx.drawImage(video, 0, 0),
		200);
	};

	/**
	 * Handeling a image file input event
	 * and converting it into base64.
	 * @param file
	 */
	const grabPhotoFromCameraRoll = (file: File): void => {
		setIsConvertingFile(true);
		convertFileToBase64(file)
			.then(image => {
				setImageData([new Image(image as string), ...imageData]);
				setIsConvertingFile(false);
			})
			.catch(error => {
				console.error(error);
				setIsConvertingFile(false);
			});
	}

	/**
	 * Takes a photo, and adds it to the photo list
	 */
	const takePhoto = () => {
		let photo: any = photoRef.current;
		const data: string = photo.toDataURL('image/jpeg');

		setImageData([new Image(data), ...imageData]);
	};

	/**
	 * Removes the selected image from the array.
	 * @param imageIndex
	 */
	const removeImage = (imageIndex: number) => {
		const list = [...imageData];
		const image = list[imageIndex];
		list.splice(imageIndex, 1);
		setImageData([...list]);
		if (onImageRemoved) {
			onImageRemoved(image);
		}
	};

	/**
	 * Used for non mobile devices
	 * @param mediaDeviceInfo
	 */
	const updateSelectedSource = (mediaDeviceInfo: MediaDeviceInfo) => {
		const {
			deviceId,
			groupId,
			kind,
			label,
		} = mediaDeviceInfo;
		patch(SettingKey.VIDEO_SOURCE, {
			deviceId,
			groupId,
			kind,
			label,
		});
	};

	/**
	 * Checking for the enumerated devices that is of the type video input
	 */
	const getAvailableCameras = async () => {
		if (isOnMobile) {
			return;
		}
		await navigator.mediaDevices.enumerateDevices()
			.then(devices => {
				const cameras = devices.filter(device => device.kind === 'videoinput');
				setAvailableSources(cameras);
				if (!settings?.videoSource) {
					updateSelectedSource(cameras[0]);
				}
			})
			.catch(console.error);
	};

	/**
	 * Unsubscribing to the camera.
	 */
	const unsubscribeToCamera = () => {
		if (videoStream && !isOnMobile) {
			videoStream.getTracks().forEach(track => track.stop());
			setVideoStream(undefined);
		}
	};

	/**
	 * Unsubscribing to the video stream
	 */
	const subscribeToVideo = () => {
		if (videoRef && !isStartingCamera && !isOnMobile) {
			setIsStartingCamera(true);
			const constraints: MediaStreamConstraints = {
				video: {
				},
			};

			if (
				settings?.videoSource?.deviceId &&
				availableSources.filter(source =>
					source.deviceId === settings?.videoSource?.deviceId).length
			) {
				constraints.video = {
					deviceId: settings?.videoSource?.deviceId,
				};
			}

			navigator.mediaDevices
				.getUserMedia(constraints)
				.then(stream => {
					const video: HTMLVideoElement = videoRef.current as any;
					if (video) {
						const {
							height = 0,
							width = 0,
						} = stream.getVideoTracks()
							[0]
							.getSettings();

						setVideoResolution({
							height,
							width,
						});

						setVideoStream(stream);

						video.srcObject = stream;
						video.play()
							.catch(console.error)
							.finally(() => setIsStartingCamera(false));
					}
				})
				.catch(err => {
					console.error('error:', err);
				});
		}
		else {
			unsubscribeToCamera();
		}
	};

	useEffect(() => {
		getAvailableCameras();
	}, []);

	useEffect(() => {
		if(settings?.videoSource && videoRef) {
			unsubscribeToCamera();
			subscribeToVideo();
		}
	}, [settings?.videoSource])

	useEffect(() => {
		subscribeToVideo();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [videoRef]);

	useEffect(() => {
		return () => unsubscribeToCamera();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [videoStream]);

	useEffect(() => {
		onPhotoTaken(imageData);
	}, [onPhotoTaken, imageData]);

	return (
		<div className="webcam-video">
			{isOnMobile ? (
				<div className="mobile-capture-image">
					<label
						htmlFor="image-input-field"
					>
						<img
							src={CameraIcon}
							alt="Ta bilde"
						/>
						<Typography type={TypographyType.textBaseSemiBold}>
						Ta bilde
						</Typography>
					</label>
					<input
						id="image-input-field"
						type="file"
						accept="image/*"
						capture="environment"
						onChange={({
							target,
						}) => {
							const list: string[] = [];
							const value = target?.files || [];
							for (let i = 0; i < value.length; i++) {
								grabPhotoFromCameraRoll(value[i]);
							}
						}}
					/>
				</div>
			) : (
				/* Not relevant, because this is the user's webcam/phone camera */
				/* eslint-disable-next-line jsx-a11y/media-has-caption */
				<video
					className="camera"
					onCanPlay={() => paintToCanvas()}
					ref={videoRef}
				/>
			)}
			<div className="camera-roll">
				{isConvertingFile && (
					<LoadingWheel message="Komprimerer…" />
				)}
				{imageData.map((image, index) => (
					<div
						key={`image-container-${index + 1}`}
						className="photo-container"
					>
						<Button
							color={ButtonColor.COMPACT}
							onClick={() => removeImage(index)}
							onMouseEnter={() => setHoverIndex(index)}
							onMouseLeave={() => setHoverIndex(-1)}
						>
							<MdOutlineClose size={'21px'} color={Colors.neutrals.tints.gy100.hex} />
						</Button>
						<img
							className={`photo${hoverIndex === index ? ' hover' : ''}`}
							src={image.content}
							alt="Foto"
						/>
					</div>
				))}
			</div>
			{/*The canvas is needed, in order to take the picture*/}
			<canvas
				ref={photoRef}
				className="photo-canvas"
				width={videoResolution?.width || 1920}
				height={videoResolution?.height || 1080}
			/>
			<div>
				{!isOnMobile && (
					<>
						<select
							value={settings?.videoSource?.deviceId}
							onChange={value =>
								updateSelectedSource(
									availableSources.filter(s => s.deviceId === value.target.value)[0]
								)}
						>
							{availableSources.map(source => (
								<option
									key={source.deviceId}
									value={source.deviceId}
								>
									{source.label}
								</option>
							))}
						</select>
						<div className="flex-button-container">
							<Button
								className="ml-0 mr-0"
								color={ButtonColor.PRIMARY}
								onClick={() => takePhoto()}
							>
								Ta bilde
							</Button>
						</div>
					</>
				)}
			</div>
		</div>
	)
};

export default Camera;
